import { apiRequest } from "./queryClient";
import { User, Company } from "@shared/types";
import type { FilesResponse, AutodeskConnectionStatus } from "./types";

export { apiRequest };
export type { User, Company, FilesResponse };

export interface Settings {
  autodeskClientId?: string;
  autodeskClientSecret?: string;
  [key: string]: any;
}

export const getSettings = async (): Promise<Settings> => {
  return await apiRequest<Settings>("/api/settings");
};

export const updateSettings = async (settings: Settings): Promise<Settings> => {
  return await apiRequest<Settings>("/api/settings", {
    method: "POST",
    body: JSON.stringify(settings),
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export interface AccAccount {
  id: number;
  accountId: string;
  name?: string;
  platform?: string;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AutodeskProject {
  id: string;
  name: string;
  accountId: string;
  status: string;
  platform: string;
}

export const registerAccAccount = async (account: {
  accountId: string;
  name?: string;
  platform?: string;
  status?: string;
}): Promise<AccAccount> => {
  const response = await fetch("/api/acc-accounts/register", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(account),
  });
  if (!response.ok) {
    throw new Error("Failed to register ACC account");
  }
  return response.json();
};

export const getAccAccounts = async (): Promise<AccAccount[]> => {
  const response = await fetch("/api/acc-accounts");
  if (!response.ok) {
    throw new Error("Failed to fetch ACC accounts");
  }
  const data = await response.json();
  return data.accounts;
};

export const getAutodeskProjects = async (): Promise<AutodeskProject[]> => {
  const response = await fetch("/api/autodesk/projects");
  if (!response.ok) {
    throw new Error("Failed to fetch Autodesk projects");
  }
  return response.json();
};

export interface File {
  id: string;
  name: string;
  type: string;
  size: number;
  status: string;
  uploadedDaysAgo: number;
  company: string;
  companyId: string;
  accUrl: string;
}

export interface CompanyResponse {
  items: Company[];
  total: number;
  hasMore: boolean;
}

export async function getCompanies(
  options: { search?: string; page?: number } = {},
): Promise<CompanyResponse> {
  const { search = "", page = 1 } = options;
  const response = await fetch(`/api/activities-data?type=companies&search=${search}&page=${page}`);
  if (!response.ok) {
    throw new Error("Failed to fetch companies");
  }
  return response.json();
}

export const getCompanyFiles = async (
  companyId: string,
): Promise<FilesResponse> => {
  const response = await fetch(`/api/companies/${companyId}/files`);
  if (!response.ok) {
    throw new Error("Failed to fetch company files");
  }
  return response.json();
};

export interface HealthScore {
  score: number;
  categories: {
    name: string;
    score: number;
    distribution: {
      high: number;
      medium: number;
      low: number;
    };
  }[];
}

export interface HealthResponse {
  overall: HealthScore;
  quality: HealthScore;
  safety: HealthScore;
  schedule: HealthScore;
  cost: HealthScore;
  issues: {
    id: string;
    title: string;
    status: string;
    priority: string;
    category: string;
  }[];
}

export async function getProjectHealth(): Promise<HealthResponse> {
  const response = await fetch("/api/project-health");
  if (!response.ok) {
    throw new Error("Failed to fetch project health");
  }
  return response.json();
}

export async function getProjectFiles(
  projectId: string,
): Promise<FilesResponse> {
  const response = await fetch(`/api/project-files?projectId=${projectId}`);
  if (!response.ok) {
    throw new Error("Failed to fetch project files");
  }
  return response.json();
}

export async function getAutodeskConnectionStatus(): Promise<AutodeskConnectionStatus> {
  const response = await fetch("/api/autodesk-connection-status");
  if (!response.ok) {
    throw new Error("Failed to fetch Autodesk connection status");
  }
  return response.json();
}
